import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { MeetingNoteView } from '@homework/shared';
import { api } from './api';
import { useMe } from './auth';
import { useCalls } from './calls';
import { useSocketEvent } from './realtime';

/**
 * Mochi visiting meeting rooms: who sent it where, whether you are being
 * asked, and — when you said yes — your browser turning your own speech into
 * text for it. Mochi never receives audio, only the words your browser heard
 * you say while you are in that room with your microphone on.
 */

export type ActiveNote = MeetingNoteView & { inRoom: boolean };

interface MeetingNotesValue {
  notes: ActiveNote[];
  /** Your own Mochi's current visit, if it is out. */
  myVisit: ActiveNote | null;
  /** Your latest visit that came back in the last two hours, for "I'm back". */
  justBack: ActiveNote | null;
  send: (roomId: string, focus?: string) => Promise<void>;
  consent: (noteId: string, included: boolean) => Promise<void>;
  recall: (noteId: string) => Promise<void>;
  cancel: (noteId: string) => Promise<void>;
  refresh: () => void;
  /** Whether this browser can transcribe at all. */
  speechSupported: boolean;
  /** What this browser is doing right now for an included visit. */
  listening: 'off' | 'on' | 'paused-muted' | 'error';
  language: string;
  setLanguage: (language: string) => void;
}

const MeetingNotesContext = createContext<MeetingNotesValue | null>(null);

// The Web Speech API is still vendor-prefixed in Chromium and Safari.
type Recognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  start: () => void;
  stop: () => void;
};
function recognitionClass(): (new () => Recognition) | null {
  const w = window as unknown as { SpeechRecognition?: new () => Recognition; webkitSpeechRecognition?: new () => Recognition };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export const SPEECH_LANGUAGES: Array<{ code: string; label: string }> = [
  { code: 'en-US', label: 'English' },
  { code: 'ar-SA', label: 'العربية' },
  { code: 'fr-FR', label: 'Français' },
];

function defaultLanguage(): string {
  try {
    const saved = localStorage.getItem('homework:speech-language');
    if (saved) return saved;
  } catch {
    // Storage can be unavailable; fall through.
  }
  return navigator.language?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US';
}

export function MeetingNotesProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  const { muted } = useCalls();
  const [notes, setNotes] = useState<ActiveNote[]>([]);
  const [language, setLanguageState] = useState(defaultLanguage);
  const [listening, setListening] = useState<MeetingNotesValue['listening']>('off');

  const load = useCallback(async () => {
    try {
      const response = await api.get<{ items: ActiveNote[] }>('/meetings/notes/active');
      setNotes(response.items);
    } catch {
      // Keep what we had; the next change or poll tries again.
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);
  useSocketEvent('meetingNotes:changed', () => void load());

  // Whether you are in a room is the server's call; check it while any visit is on.
  const anyActive = notes.some((note) => note.status === 'ACTIVE');
  useEffect(() => {
    if (!anyActive) return;
    const timer = window.setInterval(() => void load(), 8000);
    return () => window.clearInterval(timer);
  }, [anyActive, load]);

  const setLanguage = useCallback((code: string) => {
    setLanguageState(code);
    try {
      localStorage.setItem('homework:speech-language', code);
    } catch {
      // Not remembered; still applies now.
    }
  }, []);

  // ---- transcription, for the one visit you are in and said yes to
  const recording = notes.find((note) => note.status === 'ACTIVE' && note.inRoom && note.myConsent === true) ?? null;
  const Speech = useMemo(() => (typeof window === 'undefined' ? null : recognitionClass()), []);
  const recognitionRef = useRef<Recognition | null>(null);
  const shouldRun = Boolean(recording && Speech && !muted);
  const noteId = recording?.id ?? null;

  useEffect(() => {
    if (!recording) {
      setListening('off');
      return;
    }
    if (muted) setListening('paused-muted');
  }, [recording, muted]);

  useEffect(() => {
    if (!shouldRun || !Speech || !noteId) return;
    let stopped = false;
    const recognition = new Speech();
    recognition.lang = language;
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        if (!result?.isFinal) continue;
        const text = result[0].transcript.trim();
        if (text) void api.post(`/meetings/notes/${encodeURIComponent(noteId)}/utterances`, { text }).catch(() => undefined);
      }
    };
    recognition.onerror = (event) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        stopped = true;
        setListening('error');
      }
    };
    // Recognition stops itself after silence; keep it going for the meeting.
    recognition.onend = () => {
      if (!stopped) {
        try {
          recognition.start();
        } catch {
          // Already restarting.
        }
      }
    };
    try {
      recognition.start();
      setListening('on');
    } catch {
      setListening('error');
    }
    recognitionRef.current = recognition;
    return () => {
      stopped = true;
      recognition.onend = null;
      try {
        recognition.stop();
      } catch {
        // Already stopped.
      }
      recognitionRef.current = null;
    };
  }, [shouldRun, Speech, noteId, language]);

  const value = useMemo<MeetingNotesValue>(() => {
    const mine = notes.filter((note) => note.sender.id === me.user.id);
    const myVisit = mine.find((note) => note.status === 'ACTIVE' || note.status === 'SUMMARIZING') ?? null;
    const recentCutoff = Date.now() - 2 * 60 * 60_000;
    const justBack =
      mine.find((note) => (note.status === 'DONE' || note.status === 'FAILED') && note.endedAt && Date.parse(note.endedAt) > recentCutoff) ?? null;
    return {
      notes,
      myVisit,
      justBack,
      send: async (roomId, focus) => {
        await api.post('/meetings/notes', { roomId, focus: focus || undefined });
        await load();
      },
      consent: async (id, included) => {
        await api.post(`/meetings/notes/${encodeURIComponent(id)}/consent`, { included });
        await load();
      },
      recall: async (id) => {
        await api.post(`/meetings/notes/${encodeURIComponent(id)}/recall`);
        await load();
      },
      cancel: async (id) => {
        await api.post(`/meetings/notes/${encodeURIComponent(id)}/cancel`);
        await load();
      },
      refresh: () => void load(),
      speechSupported: Boolean(Speech),
      listening,
      language,
      setLanguage,
    };
  }, [notes, me.user.id, load, Speech, listening, language, setLanguage]);

  return <MeetingNotesContext.Provider value={value}>{children}</MeetingNotesContext.Provider>;
}

export function useMeetingNotes(): MeetingNotesValue {
  const value = useContext(MeetingNotesContext);
  if (!value) throw new Error('useMeetingNotes must be used inside <MeetingNotesProvider>');
  return value;
}

export function useOptionalMeetingNotes(): MeetingNotesValue | null {
  return useContext(MeetingNotesContext);
}
